import { Editor, useEditorState } from "@tiptap/react";
import React, { useEffect, useRef, useState } from "react";
import { IconType } from "react-icons";
import {
  FaAlignCenter,
  FaAlignJustify,
  FaAlignLeft,
  FaAlignRight,
  FaBold,
  FaCaretDown,
  FaFillDrip,
  FaImage,
  FaIndent,
  FaItalic,
  FaLink,
  FaListOl,
  FaListUl,
  FaOutdent,
  FaRedo,
  FaRemoveFormat,
  FaUndo,
} from "react-icons/fa";
import styled from "styled-components";
import { typeSize } from "../../theme";
import { changeIndent } from "./extensions";

export type MergeField = {
  description: string;
  value: string;
};

/** The light highlight colours from TinyMCE's default palette. */
const HIGHLIGHTS = [
  { name: "Yellow", value: "#FBEEB8" },
  { name: "Green", value: "#BFEDD2" },
  { name: "Blue", value: "#C2E0F4" },
  { name: "Red", value: "#F8CAC6" },
  { name: "Purple", value: "#ECCAFA" },
  { name: "Grey", value: "#ECF0F1" },
];

const BLOCKS = [
  { value: "p", label: "Paragraph" },
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
  { value: "h4", label: "Heading 4" },
  { value: "h5", label: "Heading 5" },
  { value: "h6", label: "Heading 6" },
  { value: "pre", label: "Preformatted" },
];

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
  border-bottom: 1px solid ${(p) => p.theme.colors.grey3};
  background: ${(p) => p.theme.colors.grey6};
`;

const Divider = styled.span`
  width: 1px;
  align-self: stretch;
  margin: 4px 4px;
  background: ${(p) => p.theme.colors.grey3};
`;

const ToolButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 30px;
  height: 30px;
  padding: 0 7px;
  border: 0;
  border-radius: 4px;
  background: ${(p) => (p.active ? p.theme.colors.grey4 : "transparent")};
  color: ${(p) => p.theme.colors.body};
  font-size: ${typeSize("small")};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${(p) => p.theme.colors.grey4};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary};
    outline-offset: 1px;
  }
`;

ToolButton.defaultProps = { type: "button" };

const BlockSelect = styled.select`
  height: 30px;
  padding: 0 6px;
  border: 1px solid ${(p) => p.theme.colors.grey3};
  border-radius: 4px;
  background: white;
  color: ${(p) => p.theme.colors.body};
  font-size: ${typeSize("small")};
`;

const MenuWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const MenuList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  min-width: 180px;
  max-height: 320px;
  overflow-y: auto;
  padding: 4px;
  border: 1px solid ${(p) => p.theme.colors.grey3};
  border-radius: 6px;
  background: white;
  box-shadow: ${(p) => p.theme.shadows.pop};
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border: 0;
  border-radius: 4px;
  background: none;
  color: ${(p) => p.theme.colors.body};
  font-size: ${typeSize("small")};
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${(p) => p.theme.colors.grey5};
    outline: none;
  }
`;

MenuItem.defaultProps = { type: "button" };

const Swatch = styled.span<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: ${(p) => p.color};
`;

const LinkRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border-bottom: 1px solid ${(p) => p.theme.colors.grey3};

  input {
    flex: 1 1 220px;
    height: 30px;
    padding: 0 8px;
    border: 1px solid ${(p) => p.theme.colors.grey3};
    border-radius: 4px;
    font-size: ${typeSize("small")};
  }
`;

const LinkError = styled.span`
  flex-basis: 100%;
  color: ${(p) => p.theme.colors.danger};
  font-size: ${typeSize("small")};
`;

/** Keeps the editor's selection when a toolbar button is clicked. */
const keepSelection = (e: React.MouseEvent) => e.preventDefault();

type ButtonProps = {
  label: string;
  icon: IconType;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
};

function Tool({ label, icon: Icon, onClick, active, disabled }: ButtonProps) {
  return (
    <ToolButton
      aria-label={label}
      title={label}
      aria-pressed={active}
      active={active}
      disabled={disabled}
      onMouseDown={keepSelection}
      onClick={onClick}
    >
      <Icon aria-hidden />
    </ToolButton>
  );
}

/** A toolbar button that opens a small menu. */
function Menu({
  label,
  button,
  children,
}: {
  label: string;
  button: React.ReactNode;
  children: (close: () => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <MenuWrap ref={ref}>
      <ToolButton
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseDown={keepSelection}
        onClick={() => setOpen((o) => !o)}
      >
        {button}
        <FaCaretDown aria-hidden size={10} />
      </ToolButton>
      {open && (
        <MenuList role="menu" onMouseDown={keepSelection}>
          {children(() => setOpen(false))}
        </MenuList>
      )}
    </MenuWrap>
  );
}

/**
 * Web, email and phone links only. Adds https:// to bare addresses like
 * "example.com"; any other scheme (javascript:, data: …) gives null.
 */
function normaliseHref(raw: string): string | null {
  const href = raw.trim();
  if (href === "") return "";
  if (/^(https?:|mailto:|tel:|#|\/)/i.test(href)) return href;
  if (/^[a-z][a-z0-9+.-]*:/i.test(href) && !/^[^:]+:\d/.test(href)) {
    return null;
  }
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(href)) return `mailto:${href}`;
  return `https://${href}`;
}

let idCounter = 0;

function currentBlock(e: Editor): string {
  if (e.isActive("codeBlock")) return "pre";
  const level = [1, 2, 3, 4, 5, 6].find((l) =>
    e.isActive("heading", { level: l }),
  );
  return level != null ? `h${level}` : "p";
}

type ToolbarProps = {
  editor: Editor;
  mergeFields?: MergeField[];
  onPickImage?: () => void;
};

export function Toolbar({ editor, mergeFields, onPickImage }: ToolbarProps) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      block: currentBlock(e),
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      link: e.isActive("link"),
      href: (e.getAttributes("link").href as string | undefined) ?? "",
      left: e.isActive({ textAlign: "left" }),
      center: e.isActive({ textAlign: "center" }),
      right: e.isActive({ textAlign: "right" }),
      justify: e.isActive({ textAlign: "justify" }),
      bullet: e.isActive("bulletList"),
      ordered: e.isActive("orderedList"),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });

  const [linkOpen, setLinkOpen] = useState(false);
  const [href, setHref] = useState("");
  const [hrefError, setHrefError] = useState(false);
  const linkInput = useRef<HTMLInputElement>(null);
  const wasLinkOpen = useRef(false);
  const linkErrorId = useRef(`rte-link-error-${++idCounter}`).current;

  // Focus the address box on open, and give focus back to the text on close.
  useEffect(() => {
    if (linkOpen) linkInput.current?.focus();
    else if (wasLinkOpen.current) editor.commands.focus();
    wasLinkOpen.current = linkOpen;
  }, [linkOpen, editor]);

  const chain = () => editor.chain().focus();

  const setBlock = (value: string) => {
    if (value === "p") chain().setParagraph().run();
    else if (value === "pre") chain().setCodeBlock().run();
    else {
      const level = Number(value.slice(1)) as 1 | 2 | 3 | 4 | 5 | 6;
      chain().setHeading({ level }).run();
    }
  };

  const openLink = () => {
    setHref(state.href);
    setHrefError(false);
    setLinkOpen(true);
  };

  const applyLink = () => {
    const url = normaliseHref(href);
    if (url === null) {
      setHrefError(true);
      return;
    }
    if (url === "") {
      chain().extendMarkRange("link").unsetLink().run();
    } else if (editor.state.selection.empty && !state.link) {
      // Nothing selected: insert the address itself as the link text.
      chain()
        .insertContent({
          type: "text",
          text: href.trim(),
          marks: [{ type: "link", attrs: { href: url } }],
        })
        .run();
    } else {
      chain().extendMarkRange("link").setLink({ href: url }).run();
    }
    setLinkOpen(false);
  };

  const removeLink = () => {
    chain().extendMarkRange("link").unsetLink().run();
    setLinkOpen(false);
  };

  return (
    <>
      <Bar role="toolbar" aria-label="Formatting">
        <Tool
          label="Undo"
          icon={FaUndo}
          disabled={!state.canUndo}
          onClick={() => chain().undo().run()}
        />
        <Tool
          label="Redo"
          icon={FaRedo}
          disabled={!state.canRedo}
          onClick={() => chain().redo().run()}
        />
        <Divider />
        <BlockSelect
          aria-label="Text style"
          value={state.block}
          onChange={(e) => setBlock(e.target.value)}
        >
          {BLOCKS.map((b) => (
            <option key={b.value} value={b.value}>
              {b.label}
            </option>
          ))}
        </BlockSelect>
        <Divider />
        <Tool
          label="Link"
          icon={FaLink}
          active={state.link || linkOpen}
          onClick={() => (linkOpen ? setLinkOpen(false) : openLink())}
        />
        {onPickImage && (
          <Tool label="Insert image" icon={FaImage} onClick={onPickImage} />
        )}
        <Divider />
        <Tool
          label="Bold"
          icon={FaBold}
          active={state.bold}
          onClick={() => chain().toggleBold().run()}
        />
        <Tool
          label="Italic"
          icon={FaItalic}
          active={state.italic}
          onClick={() => chain().toggleItalic().run()}
        />
        <Menu label="Highlight colour" button={<FaFillDrip aria-hidden />}>
          {(close) => (
            <>
              {HIGHLIGHTS.map((c) => (
                <MenuItem
                  key={c.value}
                  role="menuitem"
                  onClick={() => {
                    chain().setBackgroundColor(c.value).run();
                    close();
                  }}
                >
                  <Swatch color={c.value} aria-hidden />
                  {c.name}
                </MenuItem>
              ))}
              <MenuItem
                role="menuitem"
                onClick={() => {
                  chain().unsetBackgroundColor().run();
                  close();
                }}
              >
                <Swatch color="white" aria-hidden />
                No colour
              </MenuItem>
            </>
          )}
        </Menu>
        <Divider />
        <Tool
          label="Align left"
          icon={FaAlignLeft}
          active={state.left}
          onClick={() => chain().setTextAlign("left").run()}
        />
        <Tool
          label="Align centre"
          icon={FaAlignCenter}
          active={state.center}
          onClick={() => chain().setTextAlign("center").run()}
        />
        <Tool
          label="Align right"
          icon={FaAlignRight}
          active={state.right}
          onClick={() => chain().setTextAlign("right").run()}
        />
        <Tool
          label="Justify"
          icon={FaAlignJustify}
          active={state.justify}
          onClick={() => chain().setTextAlign("justify").run()}
        />
        <Divider />
        <Tool
          label="Bulleted list"
          icon={FaListUl}
          active={state.bullet}
          onClick={() => chain().toggleBulletList().run()}
        />
        <Tool
          label="Numbered list"
          icon={FaListOl}
          active={state.ordered}
          onClick={() => chain().toggleOrderedList().run()}
        />
        <Tool
          label="Decrease indent"
          icon={FaOutdent}
          onClick={() => changeIndent(editor, -1)}
        />
        <Tool
          label="Increase indent"
          icon={FaIndent}
          onClick={() => changeIndent(editor, 1)}
        />
        <Divider />
        <Tool
          label="Clear formatting"
          icon={FaRemoveFormat}
          onClick={() =>
            chain().unsetAllMarks().unsetTextAlign().setParagraph().run()
          }
        />
        {mergeFields != null && mergeFields.length > 0 && (
          <>
            <Divider />
            <Menu label="Merge fields" button="Merge fields">
              {(close) =>
                mergeFields.map((f) => (
                  <MenuItem
                    key={f.value}
                    role="menuitem"
                    onClick={() => {
                      chain()
                        .insertContent({ type: "text", text: f.value })
                        .run();
                      close();
                    }}
                  >
                    {f.description}
                  </MenuItem>
                ))
              }
            </Menu>
          </>
        )}
      </Bar>
      {linkOpen && (
        <LinkRow role="group" aria-label="Link">
          <input
            ref={linkInput}
            type="text"
            inputMode="url"
            aria-label="Link address"
            aria-invalid={hrefError}
            aria-describedby={hrefError ? linkErrorId : undefined}
            placeholder="https://example.com"
            value={href}
            onChange={(e) => {
              setHref(e.target.value);
              setHrefError(false);
            }}
            onKeyDown={(e) => {
              // Not a <form>: the editor often sits inside the page's own form,
              // and a nested form would submit that one instead.
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              } else if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation(); // don't close a surrounding modal
                setLinkOpen(false);
              }
            }}
          />
          <ToolButton onClick={applyLink}>Apply</ToolButton>
          {state.link && (
            <ToolButton onClick={removeLink}>Remove link</ToolButton>
          )}
          <ToolButton onClick={() => setLinkOpen(false)}>Cancel</ToolButton>
          {hrefError && (
            <LinkError id={linkErrorId} role="alert">
              Use a web address, an email address or a phone number.
            </LinkError>
          )}
        </LinkRow>
      )}
    </>
  );
}
